// UI设置管理工具
// 用于管理标题栏和导航栏的样式设置

import siteConfig from '../config/site-config.js';
import { DEFAULT_MAP_PROVIDER_SETTINGS, normalizeMapProviderSettings } from './mapProviderSettings';

/** 全站统一手机布局判断（App.js / CSS 变量应用共用） */
export function isMobileLayout() {
  if (typeof window === 'undefined') return false;
  const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  return isMobileDevice || (isSmallScreen && hasTouchSupport);
}

/** 解析手机端导航栏两行独立高度，并同步总高度 */
export function resolveMobileNavBarHeights(navBar = {}) {
  const secondRowHeight = Number(navBar.secondRowHeight) > 0 ? Number(navBar.secondRowHeight) : 36;
  let firstRowHeight = Number(navBar.firstRowHeight);
  if (!(firstRowHeight > 0)) {
    const total = Number(navBar.height) > 0 ? Number(navBar.height) : 76;
    firstRowHeight = Math.max(24, total - secondRowHeight);
  }
  return {
    firstRowHeight,
    secondRowHeight,
    height: firstRowHeight + secondRowHeight
  };
}

class UISettingsManager {
  constructor() {
    this.storageKey = 'ui_settings';
    this.defaultSettings = {
      // PC端标题栏设置
      titleBar: {
        text: '龙泉市户外应急救援队 - 现场指挥中心',
        height: 60, // 高度（像素）
        fontSize: 24, // 字体大小（像素）
        backgroundColor: '#2c3e50', // 背景颜色
        textColor: '#ffffff', // 文字颜色
        letterSpacing: 0, // 字间距（像素）
        fontWeight: 'bold', // 字体粗细
        show: true // 是否显示
      },
      // PC端导航栏设置
      navigationBar: {
        height: 32, // 高度（像素）
        backgroundColor: '#4176a7', // 背景颜色
        textColor: '#ffffff', // 文字颜色
        fontSize: 16, // 字体大小（像素）
        linkSpacing: 32, // 链接间距 gap（像素，可为 0）
        linkPadding: 16, // 链接左右内边距（像素，可为 0；调小可更紧凑）
        show: true // 是否显示
      },
      // 手机端标题栏设置
      mobileTitleBar: {
        text: '龙泉市户外应急救援队 - 现场指挥中心',
        height: 50, // 手机端标题栏高度
        fontSize: 18, // 手机端字体大小
        backgroundColor: '#2c3e50', // 背景颜色
        textColor: '#ffffff', // 文字颜色
        letterSpacing: 0, // 字间距（像素）
        fontWeight: 'bold', // 字体粗细
        show: true // 是否显示
      },
      // 手机端导航栏设置
      mobileNavigationBar: {
        height: 76, // 手机端导航栏总高度（第一行 + 第二行）
        backgroundColor: '#4176a7', // 背景颜色
        textColor: '#ffffff', // 文字颜色
        fontSize: 14, // 导航链接字体大小
        linkSpacing: 16, // 链接间距 gap（像素，可为 0）
        linkPadding: 8, // 链接左右内边距（像素，可为 0）
        show: true, // 是否显示
        // 两行独立高度
        firstRowHeight: 40, // 第一行（导航链接）高度
        secondRowHeight: 36, // 第二行高度
        secondRowFontSize: 12, // 第二行字体大小
        // 公告设置
        announcementFontSize: 12, // 公告字体大小
        announcementColor: '#ffffff', // 公告文字颜色
        // 按钮设置
        buttonFontSize: 12, // 按钮字体大小
        buttonHeight: 24, // 按钮高度
        buttonPadding: '4px 8px' // 按钮内边距
      },
      // 大屏设置
      largeScreen: {
        titleBarHeight: 80, // 大屏时标题栏高度
        titleBarFontSize: 32, // 大屏时标题栏字体大小
        navigationBarHeight: 36, // 大屏时导航栏高度
        breakpoint: 1920 // 大屏断点（像素）
      },
      // 指挥页多底图与注记层设置。
      mapProviderSettings: normalizeMapProviderSettings(DEFAULT_MAP_PROVIDER_SETTINGS)
    };
    // 与全站 apiFetch 使用同一套 API 根地址（含 /api 前缀）
    this.apiBaseUrl = siteConfig.getApiUrl();
  }

  // 深度合并设置对象（保留嵌套的 titleBar / navigationBar 等）
  mergeSettings(base, override) {
    if (!override || typeof override !== 'object') return { ...base };
    return {
      ...base,
      ...override,
      titleBar: { ...base.titleBar, ...(override.titleBar || {}) },
      navigationBar: { ...base.navigationBar, ...(override.navigationBar || {}) },
      mobileTitleBar: { ...base.mobileTitleBar, ...(override.mobileTitleBar || {}) },
      mobileNavigationBar: { ...base.mobileNavigationBar, ...(override.mobileNavigationBar || {}) },
      largeScreen: { ...base.largeScreen, ...(override.largeScreen || {}) },
      homepageSettings: { ...(base.homepageSettings || {}), ...(override.homepageSettings || {}) },
      mapProviderSettings: normalizeMapProviderSettings({
        ...(base.mapProviderSettings || {}),
        ...(override.mapProviderSettings || {}),
        keys: {
          ...((base.mapProviderSettings && base.mapProviderSettings.keys) || {}),
          ...((override.mapProviderSettings && override.mapProviderSettings.keys) || {})
        },
        enabled: {
          ...((base.mapProviderSettings && base.mapProviderSettings.enabled) || {}),
          ...((override.mapProviderSettings && override.mapProviderSettings.enabled) || {})
        },
        customTiles: {
          ...((base.mapProviderSettings && base.mapProviderSettings.customTiles) || {}),
          ...((override.mapProviderSettings && override.mapProviderSettings.customTiles) || {})
        },
        customTileDatums: {
          ...((base.mapProviderSettings && base.mapProviderSettings.customTileDatums) || {}),
          ...((override.mapProviderSettings && override.mapProviderSettings.customTileDatums) || {})
        }
      })
    };
  }

  // 解析接口返回，无效则返回 null
  parseSettingsResponse(data) {
    if (data?.success && data.settings?.titleBar && data.settings?.navigationBar) {
      return data.settings;
    }
    return null;
  }

  // 从后端获取系统设置：公开接口（无 Key）+ 登录态补齐底图 Key
  async fetchSystemSettings() {
    try {
      let settings = null;

      // 1) 公开 UI 设置（不含图商 Key）
      try {
        const publicRes = await fetch(`${this.apiBaseUrl}/admin/ui-settings/public`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        if (publicRes.ok) {
          const data = await publicRes.json();
          const parsed = this.parseSettingsResponse(data);
          if (parsed) {
            settings = parsed;
            console.log('✅ 从公开接口获取到系统设置');
          }
        } else {
          console.log('公开 UI 设置接口失败，状态码:', publicRes.status);
        }
      } catch (publicError) {
        console.warn('公开 UI 设置接口异常:', publicError);
      }

      const token = localStorage.getItem('token');
      if (token) {
        // 2a) 管理员：完整 system-settings（含 Key）
        try {
          const authRes = await fetch(`${this.apiBaseUrl}/admin/system-settings`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache'
            }
          });
          if (authRes.ok) {
            const data = await authRes.json();
            const parsed = this.parseSettingsResponse(data);
            if (parsed) {
              console.log('✅ 从管理接口获取到系统设置');
              return parsed;
            }
          }
        } catch (authError) {
          console.warn('管理 system-settings 接口异常:', authError);
        }

        // 2b) 指挥页权限用户：仅补齐底图 Key
        if (settings) {
          try {
            const mapRes = await fetch(`${this.apiBaseUrl}/admin/map-provider-settings`, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache'
              }
            });
            if (mapRes.ok) {
              const mapData = await mapRes.json();
              if (mapData?.success && mapData.mapProviderSettings) {
                settings = this.mergeSettings(settings, {
                  mapProviderSettings: mapData.mapProviderSettings
                });
              }
            }
          } catch (mapError) {
            console.warn('底图源密钥接口异常:', mapError);
          }
        }
      }

      if (settings) return settings;

      // 3) localStorage 备用
      const localSettings = localStorage.getItem('systemSettings') || localStorage.getItem('backendSystemSettings');
      if (localSettings) {
        try {
          const parsed = JSON.parse(localSettings);
          if (parsed?.titleBar && parsed?.navigationBar) {
            console.log('✅ 使用 localStorage 备用系统设置');
            return parsed;
          }
        } catch (e) {
          console.log('localStorage 设置解析失败');
        }
      }

      console.log('使用默认设置');
      return this.defaultSettings;
    } catch (error) {
      console.error('获取系统设置出错:', error);
      return this.defaultSettings;
    }
  }

  // 获取所有设置（优先从后端获取）
  async getAllSettings() {
    try {
      // 检查是否有缓存的设置，避免频繁API调用
      const cachedSettings = localStorage.getItem('backendSystemSettings');
      const lastFetchTime = localStorage.getItem('systemSettingsLastFetch');
      const now = Date.now();
      
      // 如果缓存存在且未过期（5分钟内），直接使用缓存
      if (cachedSettings && lastFetchTime && (now - parseInt(lastFetchTime, 10)) < 300000) {
        try {
          const parsedSettings = JSON.parse(cachedSettings);
          if (parsedSettings && parsedSettings.titleBar && parsedSettings.navigationBar) {
            console.log('使用缓存的系统设置');
            return this.mergeSettings(this.defaultSettings, parsedSettings);
          }
        } catch (e) {
          console.log('缓存设置解析失败，重新获取');
        }
      }
      
      // 如果缓存被清除，说明有新的设置保存，立即重新获取
      if (!cachedSettings) {
        console.log('缓存已清除，重新获取最新设置');
      }
      
      // 首先尝试从后端获取
      const backendSettings = await this.fetchSystemSettings();
      
      // 检查后端设置是否有效
      if (backendSettings && backendSettings.titleBar && backendSettings.navigationBar) {
        const mergedSettings = this.mergeSettings(this.defaultSettings, backendSettings);
        
        // 缓存设置和获取时间
        localStorage.setItem('backendSystemSettings', JSON.stringify(backendSettings));
        localStorage.setItem('systemSettingsLastFetch', now.toString());
        // 同步一份到 ui_settings，避免 getSettingsSync 读到空默认值覆盖样式
        localStorage.setItem(this.storageKey, JSON.stringify(mergedSettings));
        
        console.log('使用后端系统设置:', mergedSettings);
        return mergedSettings;
      }
      
      // 如果后端获取失败，使用localStorage
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        try {
          const localSettings = JSON.parse(stored);
          if (localSettings && localSettings.titleBar && localSettings.navigationBar) {
            console.log('使用本地存储设置:', localSettings);
            return this.mergeSettings(this.defaultSettings, localSettings);
          }
        } catch (e) {
          console.log('localStorage设置解析失败');
        }
      }
      
      console.log('使用默认设置');
      return this.defaultSettings;
    } catch (error) {
      console.error('获取UI设置失败:', error);
      return this.defaultSettings;
    }
  }

  // 同步获取设置（优先已缓存的后端设置，避免用空默认覆盖页面样式）
  getSettingsSync() {
    try {
      const cached = localStorage.getItem('backendSystemSettings');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.titleBar && parsed?.navigationBar) {
          return this.mergeSettings(this.defaultSettings, parsed);
        }
      }
      const stored = localStorage.getItem(this.storageKey);
      return stored ? this.mergeSettings(this.defaultSettings, JSON.parse(stored)) : this.defaultSettings;
    } catch (error) {
      console.error('同步获取UI设置失败:', error);
      return this.defaultSettings;
    }
  }

  // 获取特定设置
  async getSetting(path) {
    const settings = await this.getAllSettings();
    return path.split('.').reduce((obj, key) => obj?.[key], settings);
  }

  // 更新设置
  updateSetting(path, value) {
    try {
      const settings = this.getSettingsSync();
      const keys = path.split('.');
      const lastKey = keys.pop();
      const target = keys.reduce((obj, key) => obj[key], settings);
      
      if (target && typeof target === 'object') {
        target[lastKey] = value;
        localStorage.setItem(this.storageKey, JSON.stringify(settings));
        console.log(`UI设置已更新: ${path} = ${value}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('更新UI设置失败:', error);
      return false;
    }
  }

  // 重置为默认设置
  resetToDefault() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('UI设置已重置为默认值');
      return true;
    } catch (error) {
      console.error('重置UI设置失败:', error);
      return false;
    }
  }

  // 应用设置到CSS变量
  async applySettings() {
    const settings = await this.getAllSettings();
    this.applySettingsToCSS(settings);
  }

  // 同步应用设置（用于初始化）
  applySettingsSync() {
    const settings = this.getSettingsSync();
    this.applySettingsToCSS(settings);
  }

  // 应用设置到CSS变量的核心方法
  applySettingsToCSS(settings) {
    const useMobileLayout = isMobileLayout();
    
    if (useMobileLayout) {
      // 手机端设置
      const titleBar = settings.mobileTitleBar || settings.titleBar;
      const navBar = settings.mobileNavigationBar || settings.navigationBar;
      
      // 应用手机端标题栏设置
      document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
      document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
      document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
      
      // 应用手机端导航栏设置（第一行 / 第二行独立高度）
      const mobileHeights = resolveMobileNavBarHeights(navBar);
      document.documentElement.style.setProperty('--nav-bar-height', `${mobileHeights.height}px`, 'important');
      document.documentElement.style.setProperty('--mobile-first-row-height', `${mobileHeights.firstRowHeight}px`, 'important');
      document.documentElement.style.setProperty('--mobile-second-row-height', `${mobileHeights.secondRowHeight}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
      document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
      document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 16}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 8}px`, 'important');
      
      // 手机端特有设置
      if (navBar.secondRowFontSize) {
        document.documentElement.style.setProperty('--mobile-second-row-font-size', `${navBar.secondRowFontSize}px`, 'important');
      }
      if (navBar.announcementFontSize) {
        document.documentElement.style.setProperty('--mobile-announcement-font-size', `${navBar.announcementFontSize}px`, 'important');
      }
      if (navBar.announcementColor) {
        document.documentElement.style.setProperty('--mobile-announcement-color', navBar.announcementColor, 'important');
      }
      if (navBar.buttonFontSize) {
        document.documentElement.style.setProperty('--mobile-button-font-size', `${navBar.buttonFontSize}px`, 'important');
      }
      if (navBar.buttonHeight) {
        document.documentElement.style.setProperty('--mobile-button-height', `${navBar.buttonHeight}px`, 'important');
      }
      if (navBar.buttonPadding) {
        document.documentElement.style.setProperty('--mobile-button-padding', navBar.buttonPadding, 'important');
      }
      
    } else {
      // PC端设置
      const titleBar = settings.titleBar;
      const navBar = settings.navigationBar;
      
      // 应用PC端标题栏设置
      document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
      document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
      document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
      document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
      
      // 应用PC端导航栏设置
      document.documentElement.style.setProperty('--nav-bar-height', `${navBar.height}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
      document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
      document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 32}px`, 'important');
      document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 16}px`, 'important');
    }
    
    // 计算总高度
    const titleBar = useMobileLayout ? (settings.mobileTitleBar || settings.titleBar) : settings.titleBar;
    const navBar = useMobileLayout ? (settings.mobileNavigationBar || settings.navigationBar) : settings.navigationBar;
    const titleHeight = titleBar.show !== false ? titleBar.height : 0;
    const navHeight = navBar.show === false
      ? 0
      : (useMobileLayout ? resolveMobileNavBarHeights(navBar).height : navBar.height);
    document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
    
    console.log(`系统设置已应用到CSS变量 (${useMobileLayout ? '手机端' : 'PC端'})`, {
      titleBar: {
        height: titleBar.height,
        fontSize: titleBar.fontSize,
        text: titleBar.text
      },
      navBar: {
        height: navHeight,
        fontSize: navBar.fontSize
      }
    });
  }

  // 获取CSS变量值
  getCSSVariable(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name);
  }

  // 导出设置
  exportSettings() {
    return this.getSettingsSync();
  }

  // 导入设置
  importSettings(settings) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
      this.applySettingsSync();
      console.log('UI设置已导入并应用');
      return true;
    } catch (error) {
      console.error('导入UI设置失败:', error);
      return false;
    }
  }
}

// 创建单例实例
const uiSettingsManager = new UISettingsManager();

// 暴露到全局对象，方便调试和测试
if (typeof window !== 'undefined') {
  window.uiSettingsManager = uiSettingsManager;
}

export default uiSettingsManager; 